import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Add, Edit, Delete, Book, CloudUpload } from '@mui/icons-material';
import axios from 'axios';
import { useCompany } from '../contexts/CompanyContext';

interface KnowledgeEntry {
  id: number;
  title: string;
  content: string;
  category?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

const KnowledgeBase: React.FC = () => {
  const { selectedCompany } = useCompany();
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: '',
  });

  const fetchEntries = useCallback(async () => {
    if (!selectedCompany) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/knowledge?companyId=${selectedCompany.id}`);
      setEntries(response.data);
    } catch (err: any) {
      console.error('Error fetching knowledge entries:', err);
      setError('Failed to load knowledge entries');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchEntries();
    }
  }, [selectedCompany, fetchEntries]);

  const handleOpenDialog = (entry?: KnowledgeEntry) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        title: entry.title,
        content: entry.content,
        category: entry.category || '',
        tags: entry.tags?.join(', ') || '',
      });
    } else {
      setEditingEntry(null);
      setFormData({
        title: '',
        content: '',
        category: '',
        tags: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setFormData({
      title: '',
      content: '',
      category: '',
      tags: '',
    });
  };

  const handleSaveEntry = async () => {
    if (!selectedCompany) return;

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        companyId: selectedCompany.id,
        title: formData.title,
        content: formData.content,
        category: formData.category || undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
      };

      if (editingEntry) {
        // Update existing entry
        await axios.put(`/api/knowledge/${editingEntry.id}`, payload);
      } else {
        // Create new entry
        await axios.post('/api/knowledge', payload);
      }

      handleCloseDialog();
      fetchEntries();
    } catch (err: any) {
      console.error('Error saving entry:', err);
      setError(err.response?.data?.message || 'Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (!selectedCompany) return;
    
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await axios.delete(`/api/knowledge/${entryId}?companyId=${selectedCompany.id}`);
      fetchEntries();
    } catch (err: any) {
      console.error('Error deleting entry:', err);
      setError('Failed to delete entry');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedCompany) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
      'text/markdown',
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PDF, Word, or text files only.');
      // Clear the file input on error
      event.target.value = '';
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      // Clear the file input on error
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyId', selectedCompany.id.toString());
      formData.append('category', 'Uploaded Document');

      const response = await axios.post('/api/knowledge/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(`Successfully uploaded and processed: ${file.name}`);
      
      // Add the new entry to the list immediately (optimistic update)
      setEntries(prev => [...prev, response.data]);
      
      // Clear the file input
      event.target.value = '';
    } catch (err: any) {
      console.error('Error uploading document:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload document';
      setError(`Upload failed: ${errorMessage}`);
      // Clear the file input on error
      event.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Knowledge Base
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
            disabled={!selectedCompany || uploading}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept=".pdf,.docx,.doc,.txt,.md"
              onChange={handleFileUpload}
            />
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            disabled={!selectedCompany}
          >
            Add Entry
          </Button>
        </Box>
      </Box>

      {!selectedCompany && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please select a company from the dropdown to manage knowledge base entries.
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && entries.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Company Knowledge
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No knowledge base entries yet. Add company information, brand guidelines, 
            product details, and other relevant information to help generate better content.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {entries.map((entry) => (
            <Grid item xs={12} md={6} key={entry.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Book sx={{ mr: 1, color: 'primary.main' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {entry.title}
                      </Typography>
                      {entry.category && (
                        <Chip label={entry.category} size="small" sx={{ mb: 1 }} />
                      )}
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {entry.content.substring(0, 200)}
                    {entry.content.length > 200 && '...'}
                  </Typography>

                  {entry.tags && entry.tags.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {entry.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }} 
                        />
                      ))}
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Updated: {new Date(entry.updatedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog(entry)}
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteEntry(entry.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingEntry ? 'Edit Knowledge Entry' : 'Add Knowledge Entry'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              helperText="e.g., Product Features, Brand Voice, Target Audience"
            />
            
            <TextField
              label="Content"
              fullWidth
              multiline
              rows={6}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              helperText="Detailed information about this topic"
            />

            <TextField
              label="Category"
              fullWidth
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              helperText="e.g., Product, Brand, Marketing, Technical"
            />

            <TextField
              label="Tags"
              fullWidth
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              helperText="Comma-separated tags (e.g., dental, equipment, safety)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveEntry} 
            variant="contained"
            disabled={loading || !formData.title.trim() || !formData.content.trim()}
          >
            {loading ? <CircularProgress size={24} /> : (editingEntry ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeBase;

